import React, { useContext, useEffect, useRef, useState } from 'react';
import authContext from '../../../../../AuthContext/authContext';
import { STAFF_VERIFICATION_EVENT, staffSessionRequest } from '../../../../Graph/Authentication/staffSessionClient';
import { allowRecheck, firstCheckThisLoad, forgetCleared, markCleared, wasCleared } from '../../../../Graph/Authentication/staffSessionMemory';
import './StaffSessionGate.css';

const CODE_LENGTH = 6;
const formatWait = ms => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const Spinner = () => <span className="staff-session-gate__spinner" aria-hidden="true" />;

// UX only. The backend, not this component or role cookies, grants staff access.
export default function StaffSessionGate({ children, protectedRoute = true, fallbackShell = content => content, onSignedOut,
  // Default to asking the server. Only literal false is a temporary rollout
  // hold; neither this flag nor a browser role grants staff authority.
  enabled = process.env.REACT_APP_STAFF_SESSION_VERIFICATION_REQUIRED !== 'false' }) {
  const { token, baseUrl, logout } = useContext(authContext);
  const [state, setState] = useState({ token: null, phase: 'checking' });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState('');
  const [retry, setRetry] = useState(0);
  const [nextSend, setNextSend] = useState(0);
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [focused, setFocused] = useState(false);
  const actionGeneration = useRef(0);
  const actionController = useRef(null);
  const codeInput = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const activeGate = enabled || retry > 0;
  useEffect(() => {
    if (!nextSend) return undefined;
    const timer = setTimeout(() => setNextSend(0), Math.max(0, nextSend - Date.now()));
    return () => clearTimeout(timer);
  }, [nextSend]);
  // Display clock for the resend countdown and code expiry; never gates access.
  useEffect(() => {
    if (state.phase !== 'code') return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [state.phase]);
  useEffect(() => { if (state.phase === 'code') codeInput.current?.focus(); }, [state.phase]);
  useEffect(() => {
    // A private API refused this session: forget it was cleared and ask again.
    const demand = () => { forgetCleared(tokenRef.current); setRetry(value => value + 1); };
    window.addEventListener(STAFF_VERIFICATION_EVENT, demand);
    return () => window.removeEventListener(STAFF_VERIFICATION_EVENT, demand);
  }, []);
  useEffect(() => {
    if (!token || !activeGate || !protectedRoute) {
      setBusy(false);
      setState({ token, protectedRoute, phase: 'allowed' });
      return undefined;
    }
    // A sign-in the server already cleared renders at once (no flash between
    // pages or on reload); it is still re-asked quietly once per page load.
    const known = wasCleared(token);
    if (!firstCheckThisLoad(token) && known) {
      setState({ token, protectedRoute, phase: 'allowed' });
      return undefined;
    }
    let live = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    if (known) setState({ token, protectedRoute, phase: 'allowed' });
    else { setState({ token, protectedRoute, phase: 'checking' }); setBusy(false); setCode(''); setError(''); setNextSend(0); setExpiresAt(0); }
    staffSessionRequest(baseUrl, token, '', undefined, { signal: controller.signal, requiredOnLegacy: true }).then(result => {
      const allowed = !(result.required && !result.verified);
      if (allowed) markCleared(token); else forgetCleared(token);
      if (live) setState({ token, protectedRoute, phase: allowed ? 'allowed' : 'challenge' });
    }).catch(err => {
      allowRecheck(token);
      // A failed quiet re-check keeps the page; private APIs still enforce.
      if (known) return;
      if (live) {
        setState({ token, protectedRoute, phase: 'error' });
        setError(err.status === 401 ? 'This sign-in has expired or is no longer valid. Sign out and sign in again.' : err.message);
      }
    }).finally(() => clearTimeout(timer));
    return () => { live = false; controller.abort(); clearTimeout(timer); };
  }, [token, baseUrl, retry, activeGate, protectedRoute]);
  useEffect(() => () => { actionGeneration.current++; actionController.current?.abort(); }, [token]);

  function signOut() {
    // Server revocation is best effort; a failed/expired session must never trap
    // this browser. Clearing local authentication does not grant staff access.
    actionGeneration.current++;
    actionController.current?.abort();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      Promise.resolve(staffSessionRequest(baseUrl, token, '/logout', {}, { signal: controller.signal }))
        .catch(() => {}).finally(() => clearTimeout(timer));
    } catch { clearTimeout(timer); }
    finally { forgetCleared(token); logout(); onSignedOut?.(); }
  }

  async function perform(action, value = code) {
    if (busy) return;
    const generation = ++actionGeneration.current;
    setBusy(true); setPending(action); setError('');
    const controller = new AbortController();
    actionController.current = controller;
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const result = await staffSessionRequest(baseUrl, token, action,
        action === '/verify' ? { code: value } : action === '/challenge' ? { method: 'email' } : {}, { signal: controller.signal });
      if (generation !== actionGeneration.current) return;
      if (action === '/verify' && result.verified === true) {
        setCode(''); setRetry(value => value + 1); // Re-read server state, never trust a browser flag.
      } else if (action === '/challenge') {
        if (result.verified) setRetry(value => value + 1);
        else {
          setState({ token, protectedRoute, phase: 'code' }); setNextSend(Date.now() + 60000);
          setExpiresAt(Date.now() + (Number(result.expiresInSeconds) > 0 ? result.expiresInSeconds : 600) * 1000);
          setCode(''); codeInput.current?.focus();
        }
      }
    } catch (err) {
      if (generation === actionGeneration.current) {
        // The server reports wrong and expired codes alike; the local send time
        // only picks the friendlier wording.
        setError(action === '/verify' && err.code === 'INVALID_CODE' && /^\d{6}$/.test(value)
          ? (expiresAt && Date.now() >= expiresAt ? 'This code has expired. Send a new code to continue.'
            : 'That code didn’t work. Check the latest email, or send a new code.')
          : err.message || 'Verification failed.');
        if (action === '/verify') setTimeout(() => codeInput.current?.select());
      }
    }
    finally { clearTimeout(timer); if (generation === actionGeneration.current) setBusy(false); }
  }

  function changeCode(raw) {
    const next = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (next === code) return;
    setCode(next);
    if (error && state.phase === 'code') setError('');
    // Typing, pasting or autofilling the sixth digit submits once.
    if (next.length === CODE_LENGTH && code.length !== CODE_LENGTH && !busy) perform('/verify', next);
  }

  const awaitingStaff = state.token === token && ['challenge', 'code', 'error'].includes(state.phase);
  if (!token || !activeGate || !protectedRoute || (!awaitingStaff && wasCleared(token))
    || (state.token === token && state.protectedRoute === protectedRoute && state.phase === 'allowed')) return children;
  const checking = state.token !== token || state.protectedRoute !== protectedRoute || state.phase === 'checking';
  // First check for this sign-in: a quiet page, not a card. The spinner fades
  // in only if the server is slow; sign-out stays reachable if it never answers.
  if (checking) return fallbackShell(<main className="staff-session-gate staff-session-gate--quiet" aria-busy="true">
    <p className="staff-session-gate__quiet" role="status"><Spinner />Checking your sign-in…</p>
    <button className="staff-session-gate__link staff-session-gate__signout" type="button" onClick={signOut}>Sign out</button>
  </main>);
  // Only a successful server response can identify this as a staff challenge.
  // A network/auth/policy error must not mislabel an ordinary account as staff.
  const staffChallenge = !checking && ['challenge', 'code'].includes(state.phase);
  const codePhase = !checking && state.phase === 'code';
  const resendWait = Math.max(0, nextSend - now);
  const resendLocked = busy || Date.now() < nextSend;
  const expired = codePhase && expiresAt > 0 && now >= expiresAt;
  const verifying = busy && pending === '/verify';
  const sending = busy && pending === '/challenge';
  const errorNote = error && <p className="staff-session-gate__error" id="staff-session-error" role="alert">
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5h.01" /></svg>
    <span>{error}</span>
  </p>;
  return fallbackShell(<main className="staff-session-gate">
    <section className="staff-session-gate__card" aria-labelledby="staff-verification-title" aria-busy={checking || busy}>
      <div className="staff-session-gate__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3 20 6v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Z" />
          <path d="m8.5 12 2.5 2.5 4.5-5" />
        </svg>
      </div>
      <p className="staff-session-gate__eyebrow">{staffChallenge ? 'Protected workspace' : 'Session check'}</p>
      <h1 id="staff-verification-title">{staffChallenge ? 'Verify your staff sign-in' : checking ? 'Checking your sign-in' : 'Unable to check your sign-in'}</h1>
      {checking ? <p className="staff-session-gate__status staff-session-gate__status--checking" role="status"><Spinner />Checking this session…</p> : <>
        <p className="staff-session-gate__description">{staffChallenge ? 'Admin and moderator sessions require a one-time email code. It applies to this sign-in only.' : 'We could not confirm this session. Retry the check, or sign out and sign in again.'}</p>
        {codePhase && <form className="staff-session-gate__form" noValidate onSubmit={event => { event.preventDefault(); perform('/verify'); }}>
          <label htmlFor="staff-session-code">Six-digit code sent to your account email</label>
          <div className={`staff-session-gate__slots${focused ? ' is-focused' : ''}${error || expired ? ' is-invalid' : ''}${verifying ? ' is-busy' : ''}`}>
            <input id="staff-session-code" ref={codeInput} type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*"
              className="staff-session-gate__code" aria-describedby={`staff-session-help${error ? ' staff-session-error' : ''}`}
              aria-invalid={error || expired ? true : undefined} readOnly={verifying} spellCheck={false}
              value={code} maxLength={CODE_LENGTH} onChange={event => changeCode(event.target.value)}
              onPaste={event => { const pasted = event.clipboardData?.getData('text'); if (pasted && /\d/.test(pasted)) { event.preventDefault(); changeCode(pasted); } }}
              onFocus={event => { setFocused(true); const end = event.target.value.length; event.target.setSelectionRange?.(end, end); }}
              onBlur={() => setFocused(false)} />
            {Array.from({ length: CODE_LENGTH }, (_, index) => <span key={index} aria-hidden="true"
              className={`staff-session-gate__slot${code[index] ? ' is-filled' : ''}${focused && index === Math.min(code.length, CODE_LENGTH - 1) ? ' is-active' : ''}`}>
              {code[index] || (focused && index === code.length ? <i className="staff-session-gate__caret" /> : '')}
            </span>)}
          </div>
          {errorNote}
          {expired && !error && <p className="staff-session-gate__inline-note">This code has expired. Send a new code to continue.</p>}
          <button className={`staff-session-gate__button staff-session-gate__button--primary${verifying ? ' is-loading' : ''}`} type="submit" disabled={busy || !/^\d{6}$/.test(code)}>
            {verifying && <Spinner />}{verifying ? 'Verifying…' : 'Verify session'}
          </button>
          <p className="staff-session-gate__resend">
            <span>Didn’t get it?</span>
            <button className="staff-session-gate__link" type="button" disabled={resendLocked} onClick={() => perform('/challenge')}>
              {sending ? 'Sending…' : resendWait > 0 ? <>Resend code in <span className="staff-session-gate__countdown">{formatWait(resendWait)}</span></> : 'Resend code'}
            </button>
          </p>
        </form>}
        {!codePhase && <div className="staff-session-gate__actions">
          {state.phase === 'error' ? <button className="staff-session-gate__button staff-session-gate__button--primary" type="button" disabled={busy} onClick={() => setRetry(value => value + 1)}>Retry session check</button> :
            <button className={`staff-session-gate__button staff-session-gate__button--primary${sending ? ' is-loading' : ''}`} type="button" disabled={resendLocked} onClick={() => perform('/challenge')}>
              {sending && <Spinner />}{sending ? 'Sending code…' : 'Send email code'}
            </button>}
        </div>}
        {busy && <p className="staff-session-gate__sr-only" role="status">Working securely…</p>}
        {staffChallenge && <p className="staff-session-gate__help" id="staff-session-help">
          <span><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>Codes expire in 10 minutes.</span>
          <span><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="10.5" width="14" height="9.5" rx="2" /><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" /></svg>Never share a code.</span>
        </p>}
      </>}
      {!codePhase && errorNote}
      <div className="staff-session-gate__footer">
        <button className="staff-session-gate__link staff-session-gate__signout" type="button" onClick={signOut}>Sign out</button>
      </div>
    </section>
  </main>);
}
